import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';
import { FormikProvider } from 'formik';
import closeFill from '@iconify/icons-eva/close-fill';
import roundClearAll from '@iconify/icons-ic/round-clear-all';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
// material
import {
  Box,
  Stack,
  Button,
  Drawer,
  Divider,
  IconButton,
  Typography,
  TextField,
  Autocomplete
} from '@mui/material';
//
import { useSelector } from 'react-redux';
import { useTranslation } from '../../common/LocalizationProvider';
import {
  selectDevicesArray,
  selectGroupsArray,
  selectSession,
  selectUsersArray
} from '../../createSelectorUtils';

// ----------------------------------------------------------------------

TableFilterSidebar.propTypes = {
  isOpenFilter: PropTypes.bool,
  onResetFilter: PropTypes.func,
  onOpenFilter: PropTypes.func,
  onCloseFilter: PropTypes.func,
  formik: PropTypes.object,
  clubFilter: PropTypes.bool,
  deviceFilter: PropTypes.bool,
  userFilter: PropTypes.bool,
  emailFilter: PropTypes.bool,
  statusFilter: PropTypes.bool,
  levelFilter: PropTypes.bool
};

export default function TableFilterSidebar({
  isOpenFilter,
  onResetFilter,
  onOpenFilter,
  onCloseFilter,
  formik,
  clubFilter,
  deviceFilter,
  userFilter,
  statusFilter,
  levelFilter
}) {
  const t = useTranslation();
  const { getFieldProps, setFieldValue } = formik;
  const session = useSelector(selectSession);
  const groups = useSelector(selectGroupsArray);

  const devicesArray = useSelector(selectDevicesArray);
  const devices =
    formik.values.club == null
      ? devicesArray
      : devicesArray.filter((device) =>
          formik.values.club.id === 0
            ? device.groupId === 0 ||
              session.permission.find((item) => item.groupid === device.groupId) === undefined
            : device.groupId === formik.values.club.id
        );

  const usersArray = useSelector(selectUsersArray);
  const users =
    formik.values.club == null
      ? usersArray
      : usersArray.filter(
          (user) =>
            user.permission &&
            user.permission.find((item) => item.groupid === formik.values.club.id)
        );

  const statusOptions = [
    { label: t('fields_Pilot'), value: 4 },
    { label: t('fields_Pilot+'), value: 3 },
    { label: t('fields_Instructor'), value: 2 },
    { label: t('fields_Manager'), value: 1 },
    { label: t('fields_Admin'), value: 0 }
  ];

  const levelOptions = [
    { label: t('fields_Student'), value: 'student' },
    { label: t('fields_Student_Solo'), value: 'studentSolo' },
    { label: t('fields_Licensed_Pilot'), value: 'licensedPilot' },
    { label: t('fields_Instructor'), value: 'instructor' }
  ];

  return (
    <>
      <Button
        disableRipple
        color="inherit"
        endIcon={<Icon icon={roundFilterList} />}
        onClick={onOpenFilter}
      >
        {`${t('filters')} `}
      </Button>

      <FormikProvider value={formik}>
        <Drawer
          anchor="right"
          open={isOpenFilter}
          onClose={onCloseFilter}
          PaperProps={{
            sx: { width: 280, border: 'none', overflow: 'hidden' }
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 1, py: 2 }}
          >
            <Typography variant="subtitle1" sx={{ ml: 1 }}>
              {t('filters')}
            </Typography>
            <IconButton onClick={onCloseFilter}>
              <Icon icon={closeFill} width={20} height={20} />
            </IconButton>
          </Stack>

          <Divider />

          <Stack spacing={3} sx={{ p: 3 }}>
            {clubFilter && session.administrator && (
              <Autocomplete
                {...getFieldProps('club')}
                options={groups}
                getOptionLabel={(option) => (option.name === undefined ? '' : option.name)}
                renderInput={(params) => (
                  <TextField {...params} label={t('fields_Club')} margin="none" />
                )}
                onChange={(event, value) => setFieldValue('club', value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
            {deviceFilter && (
              <Autocomplete
                fullWidth
                {...getFieldProps('device')}
                options={devices}
                getOptionLabel={(option) => (option.name === undefined ? '' : option.name)}
                renderInput={(params) => <TextField {...params} label={t('fields_Device')} />}
                onChange={(event, value) => setFieldValue('device', value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
            {userFilter && (
              <Autocomplete
                fullWidth
                {...getFieldProps('user')}
                options={users}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {`${option.prenom} ${option.name}`}
                  </li>
                )}
                getOptionLabel={(option) =>
                  option.name === undefined ? '' : `${option.prenom} ${option.name}`
                }
                renderInput={(params) => <TextField {...params} label={t('fields_User')} />}
                onChange={(event, value) => setFieldValue('user', value)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
            {statusFilter && (
              <Autocomplete
                fullWidth
                {...getFieldProps('status')}
                options={statusOptions}
                getOptionLabel={(option) => (option.label === undefined ? '' : option.label)}
                renderInput={(params) => <TextField {...params} label={t('fields_Status')} />}
                onChange={(event, value) => setFieldValue('status', value)}
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            )}
            {levelFilter && (
              <Autocomplete
                fullWidth
                {...getFieldProps('level')}
                options={levelOptions}
                getOptionLabel={(option) => (option.label === undefined ? '' : option.label)}
                renderInput={(params) => <TextField {...params} label={t('fields_Level')} />}
                onChange={(event, value) => setFieldValue('level', value)}
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            )}
          </Stack>

          <Box sx={{ p: 3 }}>
            <Button
              fullWidth
              size="large"
              type="submit"
              color="inherit"
              variant="outlined"
              onClick={onResetFilter}
              startIcon={<Icon icon={roundClearAll} />}
            >
              {t('btn_Clear')}
            </Button>
          </Box>
        </Drawer>
      </FormikProvider>
    </>
  );
}
